import { useEffect } from "react";
import { resolveAssetUrl } from "@/components/ui";
import { readableTextOn } from "@/lib/utils/color";
import { useBooth } from "../BoothContext";

/** Dream-job selection screen, styled after the supplied animated reference. */
export function ParticipantScreen() {
  const booth = useBooth();
  const participants = booth.config.participants;
  const branding = booth.config.event.branding ?? {};
  const selectionBackground = resolveAssetUrl(branding.participant_background_url);
  const selectionBackgroundIsVideo = branding.participant_background_media_type === "video"
    || /\.(mp4|webm|ogg)(?:$|\?)/i.test(selectionBackground ?? "");

  useEffect(() => { booth.playCue("participant_selection"); }, [booth]);

  return (
    <div className="kids-job-picker relative flex h-full w-full flex-col items-center overflow-hidden px-4 pb-8 pt-8 sm:px-8 sm:pt-12">
      {selectionBackground && selectionBackgroundIsVideo && (
        <video
          aria-hidden
          className="pointer-events-none absolute inset-0 h-full w-full object-cover"
          autoPlay muted loop playsInline preload="auto"
          style={{ opacity: "var(--booth-bg-opacity)" }}
        >
          <source src={selectionBackground} />
        </video>
      )}
      {selectionBackground && !selectionBackgroundIsVideo && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${selectionBackground})`, opacity: "var(--booth-bg-opacity)" }}
        />
      )}
      <div className="absolute inset-0 bg-[#5bc0eb]/20" aria-hidden />
      <header className="relative z-10 shrink-0 text-center">
        <h2 className="kids-job-picker-title font-display text-4xl font-black uppercase leading-[0.95] tracking-tight sm:text-6xl">
          {booth.say("participant_prompt", "Pick Your Dream Job")}
        </h2>
        <p className="mt-3 text-sm font-bold uppercase tracking-[0.16em] text-white drop-shadow sm:text-base">
          {booth.say("participant_hint", "Choose who you want to become")}
        </p>
      </header>
      {participants.length === 0 ? (
        <p className="relative z-10 mt-12 rounded-2xl bg-white/85 px-5 py-4 text-center text-sm font-bold text-[#17324d]">No dream jobs have been configured yet.</p>
      ) : (
        <div className="relative z-10 mt-8 grid w-full max-w-3xl grid-cols-2 gap-x-4 gap-y-6 sm:grid-cols-3 sm:gap-x-8 sm:gap-y-8">
          {participants.map((participant) => {
            const logo = resolveAssetUrl(participant.logo_path);
            const isVideo = participant.config?.media_type === "video";
            return (
              <button key={participant.slug} onClick={() => booth.selectParticipant(participant)} className="kids-job-choice group flex min-w-0 flex-col items-center gap-2">
                <span className="grid aspect-square w-[clamp(92px,18vw,170px)] place-items-center overflow-hidden rounded-full border-[7px] border-[#f4b53f] bg-[#fff9df] shadow-[0_7px_0_rgb(194_133_38_/_0.6)] transition-transform group-hover:scale-105 group-active:scale-95">
                  {logo && isVideo ? (
                    <video src={logo} autoPlay muted loop playsInline className="h-full w-full object-cover" aria-label="" />
                  ) : logo ? (
                    <img src={logo} alt="" className="h-full w-full object-contain p-2" />
                  ) : (
                    <span className="font-display text-4xl font-black" style={{ color: readableTextOn(participant.color) }}>{participant.name.slice(0, 1)}</span>
                  )}
                </span>
                <span className="kids-job-label kids-ethio-button rounded-xl px-4 py-2 text-center text-sm font-black text-white shadow-[0_4px_0_#087f3e] sm:text-base">{participant.name}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
